import { siteConfig } from "../config/site";
import Blobs from "./Blobs";

function StreamButton({ href, label, testid, icon }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      data-testid={testid}
      className="group flex items-center justify-between gap-4 rounded-full border border-[color:var(--yicel-ink)]/10 bg-white/70 backdrop-blur-md px-6 py-4 hover:bg-[#2D2A32] hover:text-white transition-all duration-300"
    >
      <span className="flex items-center gap-3 text-sm tracking-wide">
        {icon}
        {label}
      </span>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
           className="opacity-50 group-hover:opacity-100 group-hover:translate-x-1 transition-all">
        <path d="M7 17 17 7M8 7h9v9" />
      </svg>
    </a>
  );
}

export default function EPSection() {
  const { ep, images } = siteConfig;

  return (
    <section
      id="ep"
      data-testid="ep-section"
      className="relative min-h-[100svh] w-full overflow-hidden"
    >
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(160deg, #FDFBF7 0%, #FFDFD3 40%, #B9E0FF 100%)",
        }}
      />
      <Blobs variant="hero" />

      <div id="top" className="relative z-10 mx-auto max-w-7xl px-6 sm:px-10 pt-36 pb-20 sm:pt-40 sm:pb-28 grid lg:grid-cols-2 gap-12 sm:gap-16 items-center">
        <div className="relative animate-fade-up">
          <div className="absolute -inset-4 rounded-[2rem] bg-gradient-to-tr from-[#FFD1D1]/50 to-[#B2A4FF]/40 blur-2xl" />
          <div className="relative aspect-square w-full max-w-lg mx-auto rounded-[1.8rem] overflow-hidden shadow-[0_40px_80px_-30px_rgba(45,42,50,0.35)]">
            <img
              src={images.epCover}
              alt={`Portada EP ${ep.title}`}
              className="h-full w-full object-cover"
              data-testid="ep-cover"
            />
          </div>
          <div className="absolute -bottom-4 -right-2 glass rounded-full px-5 py-2 rotate-[3deg]">
            <span className="font-script text-xl text-[color:var(--yicel-ink)]">EP · {ep.year}</span>
          </div>
        </div>

        <div className="animate-fade-up" style={{ animationDelay: "0.15s" }}>
          <img
            src={images.logo}
            alt="Yicel"
            className="w-40 sm:w-52 h-auto mb-6 drop-shadow-[0_12px_30px_rgba(178,164,255,0.35)]"
            data-testid="ep-logo"
          />

          <span className="inline-block text-[0.7rem] tracking-[0.35em] uppercase text-[color:var(--yicel-violet)] font-semibold">
            El nuevo EP
          </span>
          <h2
            className="mt-3 font-display text-5xl sm:text-6xl lg:text-7xl font-medium leading-[1.02] text-ink"
            data-testid="ep-title"
          >
            {ep.title}
          </h2>
          <p
            className="mt-6 max-w-md text-base sm:text-lg leading-relaxed text-[color:var(--yicel-muted)]"
            data-testid="ep-description"
          >
            {ep.description}
          </p>

          <div className="mt-8 grid gap-3 max-w-md">
            <StreamButton
              href={ep.spotify}
              label="Escuchar en Spotify"
              testid="ep-btn-spotify"
              icon={
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0a12 12 0 1 0 0 24 12 12 0 0 0 0-24zm5.5 17.3a.75.75 0 0 1-1 .25c-2.8-1.7-6.3-2-10.4-1.1a.75.75 0 0 1-.3-1.5c4.5-.9 8.4-.6 11.5 1.3.35.2.45.65.2 1.05zm1.5-3.3a.94.94 0 0 1-1.3.3c-3.2-2-8.1-2.55-11.9-1.4a.94.94 0 1 1-.55-1.8c4.35-1.3 9.75-.7 13.45 1.6.45.25.6.85.3 1.3zm.13-3.4c-3.85-2.3-10.25-2.5-13.9-1.4a1.12 1.12 0 0 1-.65-2.15c4.25-1.3 11.3-1.05 15.75 1.6a1.12 1.12 0 0 1-1.2 1.95z"/>
                </svg>
              }
            />
            <StreamButton
              href={ep.appleMusic}
              label="Apple Music"
              testid="ep-btn-apple"
              icon={
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M16.365 1.43c0 1.14-.48 2.26-1.273 3.072-.84.86-2.212 1.52-3.339 1.43-.13-1.09.44-2.26 1.21-3.02.86-.84 2.31-1.48 3.402-1.48zM20 17.7c-.55 1.25-.81 1.81-1.52 2.92-.98 1.54-2.36 3.47-4.07 3.48-1.52.01-1.91-1-3.98-.98-2.06.02-2.49 1-4 .98-1.72-.01-3.03-1.75-4.01-3.28C.5 16.1.1 10.7 2.46 7.8c1.68-2.06 4.33-3.26 6.82-3.26 2.53 0 4.12 1.38 6.2 1.38 2.02 0 3.26-1.39 6.19-1.39 2.22 0 4.57 1.21 6.24 3.3-5.48 3-4.59 10.84-.91 9.88z"/>
                </svg>
              }
            />
            <StreamButton
              href={ep.youtube}
              label="YouTube"
              testid="ep-btn-youtube"
              icon={
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.6 3.6 12 3.6 12 3.6s-7.6 0-9.4.5A3 3 0 0 0 .5 6.2C0 8 0 12 0 12s0 4 .5 5.8A3 3 0 0 0 2.6 19.9c1.8.5 9.4.5 9.4.5s7.6 0 9.4-.5a3 3 0 0 0 2.1-2.1C24 16 24 12 24 12s0-4-.5-5.8zM9.6 15.6V8.4L15.8 12l-6.2 3.6z"/>
                </svg>
              }
            />
          </div>
        </div>
      </div>
    </section>
  );
}
